import Cookies from 'js-cookie';

// 이 저장소는 관리자 채널이며 refresh 는 서버가 HttpOnly 쿠키로만 관리한다
const CHANNEL = 'admin';
const ALLOWED_USER_TYPES = ['ADMIN', 'MAINTAINER', 'SUPER_ADMIN', 'EMPLOYEE'];
// 서버가 세션을 끝냈다고 알리는 401 코드 (그 외 오류는 세션을 끝내지 않는다)
export const SESSION_END_CODES = ['SESSION_NONE', 'SESSION_ENDED', 'SESSION_REVOKED', 'INVALID_TOKEN'];

// 같은 탭에서 갱신 요청을 하나로 모으고, 로그아웃 시 진행 중인 갱신을 취소한다
let pendingRenew = null;
let sessionGeneration = 0;
// 종료 안내를 한 번만 하기 위한 표식
let sessionEndNotified = false;

export class Jwt {
    static getUserInfo() {
        return Jwt.parseJwt(Jwt.getAccessToken());
    }

    static isMasterUser() {
        const userInfo = Jwt.getUserInfo();
        if (['SUPER_ADMIN', 'MAINTAINER'].includes(userInfo && userInfo.userType)) return true;
        return false;
    }

    static setToken(token) {
        Jwt.setAccessToken(token.accessToken);
    }

    static setAccessToken(accessToken) {
        const expiryDate = Jwt.getExpiryDate(accessToken); // 만료 날짜를 계산
        Cookies.set('accessToken', accessToken, { expires: expiryDate });
        sessionEndNotified = false; // 새 세션이 생기면 다음 종료를 다시 안내한다
    }

    static getAccessToken() {
        return Cookies.get('accessToken');
    }

    // 응답 처리 시점에 현재 로그인 귀속을 확인하기 위한 세션 식별자
    static getSessionId() {
        return Jwt.parseJwt(Jwt.getAccessToken())?.sid ?? null;
    }

    static clearTokens() {
        Jwt.cancelPendingRenew();
        Cookies.remove('accessToken');
        Cookies.remove('refreshToken'); // HttpOnly 전환 이전에 남은 쿠키 정리
    }

    // 진행 중인 갱신 응답을 더 이상 저장하지 않도록 세대를 올린다
    static cancelPendingRenew() {
        sessionGeneration += 1;
        pendingRenew = null;
    }

    static isSessionEndNotified() {
        return sessionEndNotified;
    }

    static markSessionEndNotified() {
        sessionEndNotified = true;
    }

    static async isAuthenticated() {
        if (!Jwt.getAccessToken()) {
            const result = await Jwt.renewAccessToken();
            if (result !== 'renewed') return false;
        }
        const userInfo = Jwt.parseJwt(Jwt.getAccessToken());
        return !!userInfo && ALLOWED_USER_TYPES.includes(userInfo.userType);
    }

    static parseJwt(token) {
        if (!token) return null;
        const base64Url = token?.split('.')[1];
        if (!base64Url) return null;
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    }

    static getExpiryDate(token) {
        const tokenPayload = Jwt.parseJwt(token);
        const expiryDate = new Date(0);
        expiryDate.setUTCSeconds(tokenPayload.exp);
        return expiryDate;
    }

    // 갱신 요청이 시작될 때의 세션 귀속 정보로 결과를 만든다
    static #refreshResult(base, status, extra = {}) {
        return { status, code: null, accessToken: null, ...base, ...extra };
    }

    // 요청 시작 당시의 로그인이 지금도 그대로인지 확인한다 (로그아웃은 generation, 새 로그인은 sid 로 드러난다)
    static #isSameLogin(startedGeneration, startedSessionId) {
        return startedGeneration === sessionGeneration && Jwt.getSessionId() === startedSessionId;
    }

    /**
     * refresh 쿠키로 access 를 재발급한다. 같은 탭에서 실제 요청과 pending 을 1회로 모은다.
     * status: authenticated(저장 완료) | guest(SESSION_NONE) | ended(확정 종료) | failed(통신·기타 실패) | cancelled(다른 로그인/로그아웃에 밀림)
     * code: 서버 detail.code 또는 null. generation/sid: 이 결과가 유효한 로그인 상태의 식별자 (isCurrentResult 로 적용 직전 검증)
     */
    static refreshSession() {
        if (pendingRenew) return pendingRenew;
        const startedGeneration = sessionGeneration;
        const startedSessionId = Jwt.getSessionId();
        const base = { generation: startedGeneration, sid: startedSessionId };
        const renewPromise = Jwt.requestRenew()
            .then(({ status, data }) => {
                const code = data?.detail?.code ?? null;
                // 로그아웃했거나 그 사이 다른 로그인으로 바뀌었으면 성공·실패 모두 버린다
                if (!Jwt.#isSameLogin(startedGeneration, startedSessionId)) return Jwt.#refreshResult(base, 'cancelled', { code });
                const accessToken = data?.data?.accessToken;
                if ((status === 201 || data?.statusCode === 201) && accessToken) {
                    Jwt.setToken({ accessToken });
                    return Jwt.#refreshResult(base, 'authenticated', { accessToken, sid: Jwt.getSessionId() });
                }
                // 서버가 세션을 끝냈다고 한 경우에만 정리한다. 세션이 아예 없는 첫 방문은 guest 로 구분한다
                if (status === 401 && SESSION_END_CODES.includes(code)) {
                    Jwt.clearTokens();
                    // 종료 처리 자체의 정리로 바뀐 generation 은 이 결과가 유효한 상태다
                    return Jwt.#refreshResult(base, code === 'SESSION_NONE' ? 'guest' : 'ended', { code, generation: sessionGeneration, sid: null });
                }
                return Jwt.#refreshResult(base, 'failed', { code });
            })
            .catch(() => Jwt.#refreshResult(base, Jwt.#isSameLogin(startedGeneration, startedSessionId) ? 'failed' : 'cancelled'))
            .finally(() => {
                // 취소 뒤 새로 만들어진 갱신을 옛 응답이 지우지 않게 한다
                if (pendingRenew === renewPromise) pendingRenew = null;
            });
        pendingRenew = renewPromise;
        return renewPromise;
    }

    /**
     * 기존 호출자 호환: 'renewed' 저장 완료 | 'ended' 서버가 세션을 끝냄(세션 없음 포함) | 'failed' 통신 실패 | 'cancelled' 다른 로그인/로그아웃에 밀림
     */
    static renewAccessToken() {
        return Jwt.refreshSession().then(({ status }) => {
            if (status === 'authenticated') return 'renewed';
            if (status === 'guest') return 'ended';
            return status;
        });
    }

    // 네트워크 없이 현재 확정된 access 상태만 읽는다 (없으면 guest)
    static readSessionState() {
        const generation = sessionGeneration;
        const accessToken = Jwt.getAccessToken();
        if (!accessToken) return Jwt.#refreshResult({ generation, sid: null }, 'guest');
        const userInfo = Jwt.parseJwt(accessToken);
        return Jwt.#judgeUserType(userInfo, { generation, sid: userInfo?.sid ?? null, accessToken });
    }

    // 현재 로그인 상태를 화면용 결과로 돌려준다. access 가 있으면 네트워크 없이 판정하고, 없으면 refresh 로 복원을 시도한다
    static async restoreSession() {
        if (Jwt.getAccessToken()) return Jwt.readSessionState();
        const result = await Jwt.refreshSession();
        if (result.status !== 'authenticated') return result;
        return Jwt.#judgeUserType(Jwt.parseJwt(Jwt.getAccessToken()), result);
    }

    // 결과가 만들어진 로그인 상태가 지금도 그대로인지 확인한다. 화면·안내에 적용하기 직전에 호출한다
    static isCurrentResult(result) {
        return !!result && result.generation === sessionGeneration && Jwt.getSessionId() === (result.sid ?? null);
    }

    // 접근 가능 유저 권한이 아니면 쿠키를 건드리지 않고 guest 로 본다
    static #judgeUserType(userInfo, result) {
        const allowed = !!userInfo && ALLOWED_USER_TYPES.includes(userInfo.userType);
        if (allowed) return { code: null, ...result, status: 'authenticated' };
        return { ...result, status: 'guest', code: 'USER_TYPE_NOT_ALLOWED', accessToken: null };
    }

    static getSessionGeneration() {
        return sessionGeneration;
    }

    static async requestRenew() {
        const response = await fetch(`${Jwt.getApiBaseUrl()}/auth/renew-token`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ channel: CHANNEL }),
        });
        return { status: response.status, data: await response.json() };
    }

    // 서버 세션과 HttpOnly refresh 쿠키까지 끝내야 로그아웃이 성립한다
    static async logout() {
        const startedSessionId = Jwt.getSessionId();
        Jwt.cancelPendingRenew();
        const headers = { 'Content-Type': 'application/json' };
        const accessToken = Jwt.getAccessToken();
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
        const response = await fetch(`${Jwt.getApiBaseUrl()}/auth/logout`, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify({ channel: CHANNEL }),
        });
        const data = await response.json();
        if (response.status !== 201 && data?.statusCode !== 201) return false;
        const currentSessionId = Jwt.getSessionId();
        // 로그아웃하는 사이 새 로그인이 생겼다면 그 세션의 access 는 남긴다
        if (!currentSessionId || !startedSessionId || currentSessionId === startedSessionId) Jwt.clearTokens();
        return true;
    }

    static getApiBaseUrl() {
        return process.env.REACT_APP_API_BASE_URL;
    }
}
